<script setup lang="ts">
const arrowRightWhite = '/images/icons/arrow-right-white.svg'
const arrowLeftBrand = '/images/icons/arrow-left-brand.svg'
const arrowRightBrand = '/images/icons/arrow-right-brand.svg'

type ButtonType = 'Primary' | 'Secondary' | 'Ghost' | 'Destructive'
type ButtonSize = 'Small' | 'Medium' | 'Large'

const props = withDefaults(defineProps<{
  type?: ButtonType
  size?: ButtonSize
  label?: string
  showIconLeft?: boolean
  showIconRight?: boolean
  bgColor?: string
  textColor?: string
}>(), {
  type: 'Primary',
  size: 'Large',
  label: 'Button Label',
  showIconLeft: false,
  showIconRight: false,
})

const isSolid = computed(() => props.type === 'Primary' || props.type === 'Destructive')
const isInverse = computed(() => isSolid.value)
const isBrand = computed(() => props.type === 'Secondary' || props.type === 'Ghost')

const containerClass = computed(() => {
  const base = 'group inline-flex items-center justify-center cursor-pointer select-none'

  // Size-based padding & gaps
  const sizeMap: Record<ButtonSize, string> = {
    Large: 'gap-[8px] px-[12px] py-[12px]',
    Medium: 'gap-[4px] px-[24px] py-[8px]',
    Small: 'gap-[4px] px-[8px] py-[4px]',
  }

  // Type-based background / border
  const typeMap: Record<ButtonType, string> = {
    Primary: 'bg-primary',
    Destructive: 'bg-error',
    Secondary: 'bg-white border border-stroke',
    Ghost: '',
  }

  return [base, sizeMap[props.size], props.bgColor || typeMap[props.type]].join(' ')
})

const labelClass = computed(() => {
  const colorClass = props.textColor
    ? props.textColor
    : isInverse.value
      ? 'text-white'
      : 'text-brand'

  const sizeMap: Record<ButtonSize, string> = {
    Large: 'text-[16px] leading-[24px]',
    Medium: 'text-[14px] leading-[20px] tracking-[1.5px]',
    Small: 'text-[12px] leading-[16px] tracking-[0.6px]',
  }

  return `font-label font-light whitespace-nowrap ${colorClass} ${sizeMap[props.size]}`
})

const iconSize = computed(() => {
  const map: Record<ButtonSize, string> = {
    Large: 'size-[14px]',
    Medium: 'size-[12px]',
    Small: 'size-[12px]',
  }
  return `relative shrink-0 ${map[props.size]}`
})

const iconSrc = computed(() => {
  if (isInverse.value) return { left: arrowRightWhite, right: arrowRightWhite }
  return { left: arrowLeftBrand, right: arrowRightBrand }
})
</script>

<template>
  <div :class="containerClass">
    <div v-if="showIconLeft" :class="iconSize">
      <img alt="" class="block w-full h-full object-contain" :src="iconSrc.left" />
    </div>
    <p :class="labelClass">{{ label }}</p>
    <div v-if="showIconRight" :class="iconSize">
      <img alt="" class="block w-full h-full object-contain transition-transform duration-200 group-hover:translate-x-[3px]" :src="iconSrc.right" />
    </div>
  </div>
</template>
